/**
 * Aceleres -- presença do contato no WhatsApp, exibida abaixo do nome no
 * cabeçalho da conversa.
 *
 * A presença chega pela WAME, é gravada pelo Laravel em
 * whatsapp_contact_presence e espelhada no contato do Chatwoot como
 * custom_attributes. Aqui só se decide o TEXTO -- nunca se inventa presença a
 * partir de mensagem, atividade da conversa ou ação do atendimento.
 */

// O WhatsApp emite presença por TRANSIÇÃO: quando o contato fica online chega
// um `available` e mais nada até ele sair. Ou seja, o atributo do contato é um
// ESTADO, não um evento -- mas ele também não é apagado quando a assinatura
// expira, então sem uma janela um contato que esteve online uma vez ficaria
// "Online agora" para sempre.
//
// A janela acompanha o intervalo em que a assinatura é renovada enquanto a
// conversa está aberta (5 min, em Aceleres::WamePresenceSubscriber::THROTTLE).
// Passado isso não se sabe mais nada de novo, e o rótulo cai para "Visto por
// último", que continua sendo o último online do cliente -- nunca a última
// mensagem.
export const PRESENCE_FRESHNESS_MS = 5 * 60 * 1000;

// Digitar e gravar são estados de segundos, e quem os encerra é o `available`
// que a WAME manda quando a pessoa para -- o atributo é SOBRESCRITO, não
// expira. Este relógio é só rede de segurança para quando esse evento se
// perde, e por isso acompanha o mesmo intervalo do painel (15s): o WhatsApp
// reemite `composing`/`recording` a cada ~10s, então 2,5s apagavam o rótulo de
// quem continuava gravando e devolviam a palavra ao broadcast de typing, que
// não sabe distinguir gravação de digitação.
export const TYPING_FRESHNESS_MS = 15 * 1000;

// O vocabulário é o do próprio WhatsApp, minúsculas incluídas: é com ele que o
// vendedor compara o que vê aqui.
export const ONLINE_LABEL = 'online';
export const TYPING_LABEL = 'digitando...';
export const RECORDING_LABEL = 'gravando áudio...';

const HORA = new Intl.DateTimeFormat('pt-BR', { timeStyle: 'short' });
const DIA = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' });

/**
 * "hoje às 18:55", "ontem às 23:10", "em 22/08/2026 às 14:03" -- exatamente
 * como o WhatsApp escreve. Datar tudo com dd/mm/aaaa fazia o vendedor ter de
 * traduzir na cabeça um horário que quase sempre é de hoje.
 */
const quando = (date, now) => {
  const hoje = new Date(now);
  const dia = 24 * 60 * 60 * 1000;
  const meiaNoite = new Date(
    hoje.getFullYear(),
    hoje.getMonth(),
    hoje.getDate()
  ).getTime();

  if (date.getTime() >= meiaNoite) return `hoje às ${HORA.format(date)}`;
  if (date.getTime() >= meiaNoite - dia) return `ontem às ${HORA.format(date)}`;

  return `em ${DIA.format(date)} às ${HORA.format(date)}`;
};

const lastSeenLabel = (attributes, now) => {
  // Duas fontes, ambas do cliente e nenhuma do atendimento:
  //  - `last_seen_at`  : o "visto por último" que o próprio WhatsApp declara,
  //                      e que só existe para quem o deixa aberto;
  //  - `last_online_at`: o instante em que NÓS vimos a integração dizer que
  //                      ele estava online/digitando.
  // Vale a mais recente das duas. Preferir cegamente a primeira já mostrava um
  // horário velho para contato que ficou online depois -- as duas são carimbos
  // de momentos diferentes, não a mesma informação em dois campos.
  const candidates = [
    attributes.zoraxy_whatsapp_last_seen_at,
    attributes.zoraxy_whatsapp_last_online_at,
  ]
    .map(value => Date.parse(value || ''))
    .filter(value => Number.isFinite(value));
  if (candidates.length === 0) return '';

  const date = new Date(Math.max(...candidates));

  return `visto por último ${quando(date, now)}`;
};

export const whatsappPresenceLabel = (contact, now = Date.now()) => {
  if (contact?.blocked) return '';

  const attributes = contact?.custom_attributes || {};

  const presence = String(
    attributes.zoraxy_whatsapp_presence || ''
  ).toLowerCase();
  const presenceAt = Date.parse(attributes.zoraxy_whatsapp_presence_at || '');
  const age = Number.isFinite(presenceAt) ? now - presenceAt : Infinity;

  if (presence === 'composing' && age < TYPING_FRESHNESS_MS)
    return TYPING_LABEL;
  if (presence === 'recording' && age < TYPING_FRESHNESS_MS)
    return RECORDING_LABEL;
  if (
    ['available', 'composing', 'recording'].includes(presence) &&
    age < PRESENCE_FRESHNESS_MS
  )
    return ONLINE_LABEL;

  return lastSeenLabel(attributes, now);
};
